import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import { toast } from 'react-toastify';

import { useSelector } from 'react-redux';
import {
  createTrainingRoutine,
  getTrainingRoutineById,
  getWorkoutsByTrainingRoutineId,
  createWorkout,
  getBlocksByWorkoutId,
  createBlock,
  createExercise,
} from '~/api';

import { Button } from '~/components/_layouts/forms/styles';
import Dialog from '~/components/Dialog';
import {
  SDialogTitle,
  ActionButtonSection,
  defaultFieldProps,
  defaultDateProps,
} from '../../components/Dialog/styles';
import Loading from '~/components/Loading';

const FORM_INITIAL_VALUES = {
  title: '',
  goal: '',
  startDate: null,
  expectedFinishDate: null,
  observations: '',
};

export default function NewTrainingRoutineDialog({
  isOpen,
  setIsOpen,
  userId,
  refreshTrainingRoutines,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const baseTrainingRoutineId = useSelector(
    (state) => state.trainingRoutine.trainingRoutineId
  );

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const user = { id: userId };
      await createTrainingRoutine({ ...values, user });
      toast.success('Treino criado com sucesso !');

      refreshTrainingRoutines();
    } catch (error) {
      toast.error('Ops, não foi possivel criar um novo treino!');
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  const handleCopying = async () => {
    setIsLoading(true);
    try {
      const user = { id: userId };
      const { data: base } = await getTrainingRoutineById(
        baseTrainingRoutineId
      );

      delete base.id;
      const { data: trainingRoutineCopy } = await createTrainingRoutine({
        ...base,
        user,
      });

      const { data: workouts } = await getWorkoutsByTrainingRoutineId(
        baseTrainingRoutineId
      );

      workouts.map(async (workout) => {
        // Salva o id para uso futuro mas remove do objeto
        const workoutId = workout.id;
        delete workout.id;

        // Cria cópia da ficha base e atribui ao treino novo
        const { data: workoutCopy } = await createWorkout({
          ...workout,
          trainingRoutine: { id: trainingRoutineCopy.id },
        });

        // Busca blocos da ficha base e cria cópias para ficha nova
        const { data: blocks } = await getBlocksByWorkoutId(workoutId);

        blocks.map(async (block) => {
          delete block.id;

          // Cria cópia do bloco base e atribui a ficha nova
          const { data: blockCopy } = await createBlock({
            ...block,
            workout: { id: workoutCopy.id },
          });

          block.exercises.map(async (exercise) => {
            delete exercise.id;

            // Cria cópia do exercicio base e atribui ao bloco novo
            await createExercise({
              ...exercise,
              block: { id: blockCopy.id },
            });
          });
        });
      });

      toast.success('Treino atribuído com sucesso !');

      refreshTrainingRoutines();
    } catch (error) {
      toast.error('Ops, não foi possivel criar um novo treino!');
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  return (
    <Dialog open={isOpen}>
      <SDialogTitle>Novo Treino</SDialogTitle>
      <Formik initialValues={FORM_INITIAL_VALUES} onSubmit={handleSubmit}>
        <Form>
          <div>
            <Field
              name="title"
              required
              label="Nome do treino"
              {...defaultFieldProps}
            />
            <Field name="goal" label="Objetivo" {...defaultFieldProps} />
            <Field
              type="date"
              name="startDate"
              required
              label="Data de inicio"
              {...defaultDateProps}
              {...defaultFieldProps}
            />
            <Field
              type="date"
              name="expectedFinishDate"
              label="Data estimada de finalização"
              {...defaultDateProps}
              {...defaultFieldProps}
            />
            <Field
              name="observations"
              label="Observações"
              multiline
              {...defaultFieldProps}
            />
          </div>

          <ActionButtonSection>
            <Button
              disabled={isLoading}
              type="button"
              cancelButton
              onClick={() => setIsOpen(false)}
            >
              Cancelar
            </Button>
            <Button disabled={isLoading} type="submit">
              Criar
              {isLoading && <Loading buttonLoading />}
            </Button>

            {baseTrainingRoutineId && (
              <>
                <h3 style={{ marginTop: '10px' }}>
                  Existe um treino salvo, deseja atribuí-lo para este aluno?
                </h3>
                <Button
                  disabled={isLoading}
                  type="button"
                  onClick={handleCopying}
                >
                  Atribuir treino salvo
                  {isLoading && <Loading buttonLoading />}
                </Button>
              </>
            )}
          </ActionButtonSection>
        </Form>
      </Formik>
    </Dialog>
  );
}

NewTrainingRoutineDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  userId: PropTypes.number.isRequired,
  refreshTrainingRoutines: PropTypes.func.isRequired,
};
